import baseScmService from 'services/baseScmService';

const sbatGroupService = baseScmService.injectEndpoints({
  endpoints: (build) => ({
    getSbatGroupList: build.query({
      query: (superBatchId) => ({
        method: 'GET',
        url: '/sbat-groups/',
        params: { 'filter[super_batch_id]': superBatchId },
      }),
      providesTags: (result, error, superBatchId) => {
        const sbatGroupTags = result?.data?.map((sbatGroup) => ({
          type: 'SbatGroup',
          id: sbatGroup.id,
        }));
        return error
          ? []
          : [...sbatGroupTags, { type: 'SuperBatch', id: superBatchId }];
      },
    }),
    getSbatGroup: build.query({
      query: (sbatGroupId) => ({
        method: 'GET',
        url: `/sbat-groups/${sbatGroupId}`,
      }),
      providesTags: (result, error, sbatGroupId) => {
        const academyTopicId = result?.data?.academy_topic_id;
        return error
          ? []
          : [
              { type: 'AcademyTopic', id: academyTopicId },
              { type: 'SbatGroup', id: sbatGroupId },
            ];
      },
    }),
    deleteSbatGroup: build.mutation({
      query: ({ sbatGroupId, comms, reason }) => ({
        method: 'DELETE',
        url: `/sbat-groups/${sbatGroupId}/`,
        body: {
          notification_channels: comms,
          reason_for_cancellation: reason,
        },
      }),
      invalidatesTags: (result, error, { sbatGroupId }) => {
        return error ? [] : [{ type: 'SbatGroup', id: sbatGroupId }];
      },
    }),
    bulkCreateSbatGroups: build.mutation({
      query: ({
        superBatches,
        academyTopics,
        library,
        academySchedule,
        startDate,
      }) => ({
        method: 'POST',
        url: '/sbat-groups/bulk-create/',
        body: {
          super_batch_ids: superBatches.map((o) => o.value),
          academy_topics: Object.values(academyTopics)
            .filter((academyTopic) => academyTopic.selected)
            .map((academyTopic) => ({
              id: academyTopic.id,
              primary_instructor_id: academyTopic.primaryInstructor?.value,
              secondary_instructor_id: academyTopic.secondaryInstructor?.value,
              other_instructor_ids: academyTopic.otherInstructors.map(
                (o) => o.value
              ),
              date_of_topic: new Date(academyTopic.dateOfTopic),
            })),
          // Below data is only for logging purpose
          library_id: library.value,
          academy_schedule_id: academySchedule.value,
          start_date: startDate,
        },
      }),
      invalidatesTags: (_, error, { superBatches }) =>
        error
          ? []
          : superBatches.map((o) => ({ type: 'SuperBatch', id: o.value })),
    }),
    bulkUpdateSbatGroups: build.mutation({
      query: ({ sbatGroups, communications, rescheduleReason }) => ({
        method: 'PUT',
        url: '/sbat-groups/bulk-update/',
        body: {
          sbat_groups: Object.values(sbatGroups)
            .filter((sbatGroup) => !sbatGroup.locked)
            .map((sbatGroup) => ({
              id: sbatGroup.id,
              date_of_topic: new Date(sbatGroup.newDateOfTopic),
            })),
          reason: rescheduleReason?.customReason || rescheduleReason?.label,
          notification_channels: Object.keys(communications).filter(
            (key) => communications[key]
          ),
        },
      }),
      invalidatesTags: (_, error, { sbatGroups }) =>
        error
          ? []
          : Object.values(sbatGroups)
              .filter((sbatGroup) => !sbatGroup.locked)
              .map(({ id }) => ({ type: 'SbatGroup', id })),
    }),
    updateSbatGroup: build.mutation({
      query: ({ sbatGroup, newDateOfTopic, reason, notificationChannels }) => ({
        method: 'PUT',
        url: `/sbat-groups/${sbatGroup.id}`,
        body: {
          sbat_group: {
            date_of_topic: new Date(newDateOfTopic),
          },
          reason: reason,
          notification_channels: notificationChannels,
        },
      }),
      invalidatesTags: (result, error, { sbatGroup }) => {
        const sbatProjects =
          sbatGroup?.super_batch_academy_topics[0]?.active_sbat_projects;
        const superBatchProject = sbatProjects[0]?.super_batch_project;
        return error
          ? []
          : [
              { type: 'SbatGroup', id: sbatGroup.id },
              { type: 'SuperBatchProject', id: superBatchProject?.id },
            ];
      },
    }),
    ingestSbatGroupPlaylist: build.mutation({
      query: ({ sbatGroup, content }) => ({
        method: 'POST',
        url: `/sbat-groups/${sbatGroup.id}/playlists`,
        body: {
          content,
        },
      }),
      invalidatesTags: (result, error, { sbatGroup, playlistId }) =>
        error
          ? []
          : [
              { type: 'SbatGroup', id: sbatGroup.id },
              { type: 'Playlist', id: playlistId },
            ],
    }),
    bulkDeleteSbatGroups: build.mutation({
      query: ({ sbatGroupIds, reason, comms }) => ({
        method: 'DELETE',
        url: `/sbat-groups/bulk-destroy`,
        body: {
          sbat_group_ids: sbatGroupIds,
          reason: reason,
          notification_channels: comms,
        },
      }),
      invalidatesTags: (result, error, { sbatGroupIds }) => {
        const sbatGroupTags = sbatGroupIds.map((sbatGroupId) => ({
          type: 'SbatGroup',
          id: sbatGroupId,
        }));
        return error ? [] : sbatGroupTags;
      },
    }),
    bulkRemoveSuperBatches: build.mutation({
      query: ({ sbatGroupIds, superBatchIds }) => ({
        method: 'DELETE',
        url: '/sbat-groups/bulk-remove-batches',
        body: {
          sbat_group_ids: sbatGroupIds,
          super_batch_ids: superBatchIds,
        },
      }),
      invalidatesTags: (result, error, { sbatGroupIds }) => {
        const sbatGroupTags = sbatGroupIds.map((sbatGroupId) => ({
          type: 'SbatGroup',
          id: sbatGroupId,
        }));
        return error ? [] : sbatGroupTags;
      },
    }),
    bulkAddSuperBatches: build.mutation({
      query: ({ sbatGroupIds, superBatchIds }) => ({
        method: 'POST',
        url: '/sbat-groups/bulk-add-batches',
        body: {
          sbat_group_ids: sbatGroupIds,
          super_batch_ids: superBatchIds,
        },
      }),
      invalidatesTags: (result, error, { sbatGroupIds }) => {
        const sbatGroupTags = sbatGroupIds.map((sbatGroupId) => ({
          type: 'SbatGroup',
          id: sbatGroupId,
        }));
        return error ? [] : sbatGroupTags;
      },
    }),
    validateDestroy: build.mutation({
      query: ({ sbatGroupIds }) => ({
        method: 'POST',
        url: '/sbat-groups/validate-destroy',
        body: {
          sbat_group_ids: sbatGroupIds,
        },
      }),
    }),
    validateUpdate: build.mutation({
      query: ({ sbatGroupIds }) => ({
        method: 'POST',
        url: '/sbat-groups/validate-update',
        body: {
          sbat_group_ids: sbatGroupIds,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useBulkCreateSbatGroupsMutation,
  useBulkUpdateSbatGroupsMutation,
  useDeleteSbatGroupMutation,
  useGetSbatGroupListQuery,
  useIngestSbatGroupPlaylistMutation,
  useGetSbatGroupQuery,
  useUpdateSbatGroupMutation,
  useBulkDeleteSbatGroupsMutation,
  useBulkRemoveSuperBatchesMutation,
  useBulkAddSuperBatchesMutation,
  useValidateDestroyMutation,
  useValidateUpdateMutation,
} = sbatGroupService;

export default sbatGroupService;
